import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Phone, Trash2, Inbox } from "lucide-react";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
const statusBadge = { new: "bg-[#E07A5F] text-white", responded: "bg-[#81B29A] text-white", closed: "bg-gray-200 text-gray-600" };

export default function Inquiries() {
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/inquiries"), api.get("/properties")]).then(([i, p]) => {
      setItems(i.data); setProps(p.data);
      if (i.data[0]) setActive((prev) => prev || i.data[0]);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const propName = (id) => props.find((p) => p.id === id)?.name;

  const setStatus = async (inq, status) => {
    const { data } = await api.put(`/inquiries/${inq.id}/status`, { status });
    setItems((arr) => arr.map((x) => (x.id === inq.id ? data : x)));
    setActive(data);
    toast.success(`Marked as ${status}`);
  };

  const remove = async (inq) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/inquiries/${inq.id}`);
    setItems((arr) => arr.filter((x) => x.id !== inq.id));
    setActive(null);
    toast.success("Inquiry deleted");
  };

  return (
    <div data-testid="inquiries-page" className="max-w-6xl">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-2">Inbox</p>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-8">Guest Inquiries</h1>

      {loading ? (
        <div className="flex justify-center pt-20"><Loader2 className="animate-spin text-[#1A365D]" size={28} /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-gray-400 flex flex-col items-center gap-3"><Inbox size={40} strokeWidth={1.2} /> No inquiries yet.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
          <div className="bg-white rounded-2xl border border-black/5 shadow-sm overflow-hidden max-h-[70vh] overflow-y-auto">
            {items.map((inq) => (
              <button
                key={inq.id}
                data-testid={`inquiry-item-${inq.id}`}
                onClick={() => setActive(inq)}
                className={`w-full text-left px-5 py-4 border-b border-black/5 transition-colors ${active?.id === inq.id ? "bg-[#F4F1EA]" : "hover:bg-[#FDFBF7]"}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-[#1A365D]">{inq.name}</span>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${statusBadge[inq.status]}`}>{inq.status}</span>
                </div>
                <p className="text-sm text-gray-500 truncate">{inq.message}</p>
                <p className="text-xs text-gray-400 mt-1">{fmt(inq.created_at)}</p>
              </button>
            ))}
          </div>

          {active ? (
            <div data-testid="inquiry-detail" className="bg-white rounded-2xl border border-black/5 shadow-sm p-8">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="font-display text-3xl font-medium">{active.name}</h2>
                  <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                    {active.email && <span className="flex items-center gap-1.5"><Mail size={14} /> {active.email}</span>}
                    {active.phone && <span className="flex items-center gap-1.5"><Phone size={14} /> {active.phone}</span>}
                  </div>
                  {active.property_id && propName(active.property_id) && (
                    <p className="text-sm text-[#E07A5F] mt-1">Interested in: {propName(active.property_id)}</p>
                  )}
                </div>
                <button data-testid="delete-inquiry-button" onClick={() => remove(active)} className="p-2 rounded-full text-gray-400 hover:text-white hover:bg-[#EF4444] transition-colors"><Trash2 size={18} /></button>
              </div>
              <div className="mt-6 bg-[#FDFBF7] rounded-xl p-5 text-gray-700 leading-relaxed">{active.message}</div>
              <p className="text-xs text-gray-400 mt-3">{fmt(active.created_at)}</p>
              <div className="flex gap-3 mt-8">
                {active.email && (
                  <a data-testid="reply-email-button" href={`mailto:${active.email}?subject=Re: Your inquiry`} className="bg-[#1A365D] text-white hover:bg-[#2A4A7F] rounded-full px-6 py-2.5 font-medium transition-colors">Reply by Email</a>
                )}
                <button data-testid="mark-responded-button" onClick={() => setStatus(active, "responded")} className="bg-[#F4F1EA] text-[#1A365D] hover:bg-[#E3D5CA] rounded-full px-6 py-2.5 font-medium transition-colors">Mark Responded</button>
                <button data-testid="mark-closed-button" onClick={() => setStatus(active, "closed")} className="bg-[#F4F1EA] text-[#1A365D] hover:bg-[#E3D5CA] rounded-full px-6 py-2.5 font-medium transition-colors">Close</button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-black/5 shadow-sm flex items-center justify-center text-gray-400">Select an inquiry to read</div>
          )}
        </div>
      )}
    </div>
  );
}
